import { UpdateNotice } from '../components/UpdateNotice';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../lib/api';
import { useSession } from '../lib/session';
import { RealtimeProvider } from '../realtime/RealtimeProvider';
import { Sidebar } from '../components/Sidebar';
import { QuickSwitcher } from '../components/QuickSwitcher';
import { useDesktopNotifications, useSoundVolume } from '../data/notifications';
import { onServiceWorkerNavigate } from '../lib/sw-navigation';
import { NotificationNudge } from '../components/NotificationNudge';
import { unreadBadgeCount, useAppBadge } from '../data/badge';
import { ActiveTeamProvider, useTeams } from '../data/teams';
import { useChannels } from '../data/channels';
import { setUnreadBadge } from '../lib/desktop';
import { HuddleProvider } from '../huddle/HuddleProvider';
import { ChangePasswordScreen } from './change-password';
import { syncThemeFromServer } from '../lib/theme';
import { syncAppIconFromServer } from '../lib/app-icon';
import { HuddleBar, HuddleInvitePrompt } from '../components/HuddleBar';
import { ServiceHost } from '../components/ServiceHost';
import { openHuddleWindow } from '../lib/huddle-window';
import { InstallNudge } from '../components/InstallApp';
import { MissedCallBanner } from '../components/MissedCallBanner';
import { ShareTargetDialog } from '../components/ShareTargetDialog';
import { useOutboxFlusher } from '../data/outbox';
import { ReminderPopup } from '../components/ReminderPopup';
import { CalendarReminderPopup } from '../components/CalendarReminderPopup';
import { AlertPopup } from '../components/AlertPopup';

interface LayoutValue {
  sidebarCollapsed: boolean;
  expandSidebar: () => void;
}

const LayoutContext = createContext<LayoutValue>({
  sidebarCollapsed: false,
  expandSidebar: () => {},
});

export const useLayout = () => useContext(LayoutContext);

const COLLAPSE_KEY = 'avslack.sidebar.collapsed';
const WIDTH_KEY = 'avslack.sidebar.width';

/**
 * Az oldalsav szelessege - keppontban, gepenkent megjegyezve.
 *
 * A hatarok nem onkenyesek: a also alatt a csatornanevek olvashatatlanna
 * valnak, a felso folott pedig a beszelgetes szorul ossze. A megjegyzett
 * ertek is ezek koze kerul - ha egy regi vagy elrontott ertek maradt a
 * tarolóban, nem tud hasznalhatatlan feluletet okozni.
 */
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 272;

const clampWidth = (value: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function readWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : DEFAULT_WIDTH;
  } catch {
    // Privat ablakban vagy tiltott tarolasnal: az alapertelmezes.
    return DEFAULT_WIDTH;
  }
}

export function AppLayout() {
  const navigate = useNavigate();
  const session = useSession();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [width, setWidth] = useState(readWidth);
  /**
   * MOBILON ket teljes kepernyos lap valtja egymast.
   *
   * A kezdolap (`/`) a csatornak es a beszelgetesek listaja; barmi mas a
   * tartalom, ami jobbrol beusztatva elfedi. Ez a telefonos beszelgeto-
   * alkalmazasok szokasos mintaja, es azert kell, mert egy 390 keppont szeles
   * kepernyon nem fer el egymas mellett a lista es a beszelgetes.
   *
   * Asztalon ebbol semmi nem latszik: ott a ket lap egymas mellett all.
   */
  const atHome = useRouterState({ select: (state) => state.location.pathname === '/' });

  /**
   * OLVASATLAN-JELZES: egyetlen szam, harom helyen.
   *
   * Ugyanaz az ertek megy az asztali alkalmazas talcaikonjara, a telepitett
   * PWA ikonjara, es ugyanez az osszeg all ossze az oldalsav soraibol. Ha a
   * harom mast mondana, egyik sem lenne hiheto - "az ikonon 12 van, a listaban
   * ket csatorna, osszesen 5" utan senki nem nezne tobbet az ikonra.
   *
   * A nemitott beszelgetesek kimaradnak, ahogy a feluleten is.
   */
  const { data: channelList } = useChannels();
  const { data: teams } = useTeams();
  const unreadTotal = unreadBadgeCount(channelList);

  useEffect(() => {
    void setUnreadBadge(unreadTotal);
  }, [unreadTotal]);

  /**
   * A masik gepen valasztott tema ES ikon atvezetese.
   *
   * A `localStorage` gyorsitotarbol mar fest a felulet (villanas nelkul); ez
   * csak akkor ir felul, ha a szerveren mas van - vagyis ha a felhasznalo
   * masik gepen valtott.
   */
  useEffect(() => {
    const user = session.data?.user;
    if (user) {
      syncThemeFromServer(user.themeMode, user.colorScheme);
      syncAppIconFromServer(user.appIcon);
    }
  }, [session.data?.user]);

  /*
    Jelveny a telepitett alkalmazas ikonjan.

    Itt a helye es nem az oldalsavban: az oldalsav ossze is csukhato, a
    jelvenynek viszont akkor is stimmelnie kell.
  */
  useAppBadge(unreadTotal);

  // A beallitott hangero eljuttatasa a hanglejatszohoz - lasd `useSoundVolume`.
  useSoundVolume();

  const desktop = useDesktopNotifications((channelId, messageId) => {
    void navigate({ to: '/c/$channelId', params: { channelId }, search: { m: messageId } });
  });

  /*
    Ugras a RENDSZER-ERTESITESROL, ujratoltes nelkul.

    A service worker csak szol, hova kellene menni; a navigaciot itt vegezzuk
    el - ugyanugy, mint amikor az oldalsavban kattint valaki. Korabban a
    service worker maga navigalt (`client.navigate()`), ami teljes
    oldalbetoltes: oleles kozben egy emlekezteto-ertesitesre kattintva a lap
    ujraepult, es a felhasznalo KIESETT a hivasbol.
  */
  useEffect(
    () =>
      onServiceWorkerNavigate(({ channelId, messageId, call }) => {
        if (call) {
          void openHuddleWindow(channelId, { incoming: true });
          return;
        }
        void navigate({ to: '/c/$channelId', params: { channelId }, search: { m: messageId } });
      }),
    [navigate],
  );

  const unauthorized = session.error instanceof ApiRequestError && session.error.status === 401;
  const queryClient = useQueryClient();
  const [pfUzenet, setPfUzenet] = useState<string | null>(null);

  useEffect(() => {
    if (unauthorized) void navigate({ to: '/login' });
  }, [unauthorized, navigate]);

  /*
    A ProjectFlow-osszekotesbol VISSZAERKEZES.

    A szerver egy jelzovel a cimben kuld vissza ide - ez az egyetlen mod, hogy
    egy masik rendszerbol visszaterve barmit is mondjunk. A jelzot azonnal ki is
    vesszük a cimbol: egy frissites nem allithatja meg megegyszer, hogy "kesz",
    es a link megosztva sem hordozza tovabb.
  */
  useEffect(() => {
    const jelzo = new URLSearchParams(window.location.search).get('projectflow');
    if (!jelzo) return;

    window.history.replaceState(null, '', window.location.pathname);
    void queryClient.invalidateQueries({ queryKey: ['projectflow'] });
    setPfUzenet(
      jelzo === 'kesz'
        ? 'A ProjectFlow-fiókod össze van kötve.'
        : 'Az összekötés nem sikerült. Próbáld újra a beállításokban.',
    );

    const timer = setTimeout(() => setPfUzenet(null), 5000);
    return () => clearTimeout(timer);
  }, [queryClient]);

  /* Az Outlook-osszekotesbol visszaerkezes - ugyanaz a minta, mint fent (docs/23). */
  useEffect(() => {
    const jelzo = new URLSearchParams(window.location.search).get('outlook');
    if (!jelzo) return;

    window.history.replaceState(null, '', window.location.pathname);
    void queryClient.invalidateQueries({ queryKey: ['outlook'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    setPfUzenet(
      jelzo === 'kesz'
        ? 'Az Outlook-naptárad össze van kötve. A megbeszéléseid pár másodperc múlva megjelennek.'
        : jelzo === 'megszakadt'
          ? 'Az Outlook-összekötés megszakadt.'
          : 'Az Outlook-összekötés nem sikerült. Próbáld újra a beállításokban.',
    );

    const timer = setTimeout(() => setPfUzenet(null), 6000);
    return () => clearTimeout(timer);
  }, [queryClient]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // a tarolas hianya nem torheti el az elrendezest
    }
  }, [collapsed]);

  /*
    A szelesseget a HUZAS VEGEN mentjuk, nem kozben.

    Huzas kozben keppontonkent irnank a tarolóba - feleslegesen, es a
    bezaraskor amugy is csak az utolso allapot szamit.
  */
  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      // a tarolas hianya nem torheti el az elrendezest
    }
  }, [width]);

  /**
   * Az oldalsav szelenek huzasa.
   *
   * A `setPointerCapture` a lenyeg: enelkul a mutato kicsuszna a vekony
   * fogantyurol, es a huzas felbeszakadna - eppen gyors mozdulatnal, amikor a
   * legzavarobb.
   */
  const startResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (moveEvent: PointerEvent) => setWidth(clampWidth(moveEvent.clientX));
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  }, []);

  /**
   * Globalis gyorsbillentyuk.
   *
   * A SZOVEGFORMAZAS billentyui SZENTEK: Ctrl+B (felkover), Ctrl+I (dolt),
   * Ctrl+Shift+X (athuzott), Ctrl+E (kod), Ctrl+Shift+B (idezet) - ezeket
   * mindenki ismeri, es a szerkeszto hasznalja oket.
   *
   * Az oldalsav ezert Ctrl+backslash-re kerult (ahogy a szerkesztoprogramokban
   * is). Eredetileg Ctrl+B volt, ami elvette a felkoveret a szerkesztotol.
   */
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && key === 'k') {
        event.preventDefault();
        setSwitcherOpen((open) => !open);
      }
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && key === 'f') {
        event.preventDefault();
        void navigate({ to: '/search', search: {} });
      }
      if ((event.ctrlKey || event.metaKey) && key === '\\') {
        event.preventDefault();
        setCollapsed((value) => !value);
      }
      if (event.key === 'Escape') setSwitcherOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const expandSidebar = useCallback(() => setCollapsed(false), []);

  if (session.isLoading) {
    return <div className="grid h-full place-items-center text-ink-muted">Betöltés…</div>;
  }
  if (!session.data) return null;

  /**
   * Kotelezo jelszovaltas.
   *
   * Ez a chat HELYETT jelenik meg, nem folotte: egy bezarhato ablakot meg
   * lehetne kerulni, ezt nem. Az ideiglenes jelszo egy levelben utazott -
   * le kell cserelni, mielott barmit is csinal a felhasznalo.
   */
  if (session.data.mustChangePassword) return <ChangePasswordScreen />;

  const me = session.data.user;

  return (
    <ActiveTeamProvider homeTeamId={me.teamId} teams={teams ?? []}>
      <RealtimeProvider meId={me.id} onNotification={desktop.show}>
        {/*
        A huddle a LEGKULSO reteg a layouton belul: a hivas tulel minden
        csatornavaltast es utvonal-valtast. Ha a hivas egy nezethez tartozna,
        egy navigacio bontana - pontosan az a hiba, amit kerulunk.
      */}
        {/*
        A lejart emlekezteto felugro ablaka a LEGKULSO reteg: barhol allsz,
        meg kell jelennie - es akkor is, ha nemitva vagy.
      */}
        <ReminderPopup />
        <CalendarReminderPopup />
        <AlertPopup />

        {/*
        A ProjectFlow-osszekotes visszajelzese.

        Magatol eltunik: egy nyugtazando ablak egy sikeres muvelet utan csak
        egy plusz kattintas. Aki nem nezett oda, annak a beallitasokban ugyis
        ott all az allapot.
      */}
        {pfUzenet && (
          <div className="pointer-events-none fixed bottom-24 left-1/2 z-50 -translate-x-1/2">
            <p className="animate-fade-rise rounded-pill bg-ink px-4 py-2 text-sm font-medium text-surface shadow-(--shadow-modal)">
              {pfUzenet}
            </p>
          </div>
        )}
        <HuddleProvider>
          <LayoutContext.Provider value={{ sidebarCollapsed: collapsed, expandSidebar }}>
            {/*
              `overflow-clip`, NEM `overflow-hidden` - itt es a <main>-ben is.

              A `hidden` csak a felhasznalo elol zarja el a gorgetest: a
              bongeszo egy focus()-ra vagy scrollIntoView()-ra (a szerkeszto
              es a kiemelt uzenet is ilyet kul) tovabbra is elgorgeti. Eles hiba
              volt, hogy a beszelgetes lassan felcsuszott es eltunt, es
              gorgetessel nem lehetett visszahozni. A `clip` nem gorgetheto,
              programbol sem.
            */}
            <div className="relative flex h-full overflow-clip">
              {/*
              Mobilon teljes kepernyos lap, asztalon oszlop.

              Mindketto vegig a DOM-ban marad: igy nem vesz el a benne levo
              allapot (nyitott szekciok, gorgetes), es a valtas usztathato.
            */}
              <div
                /*
                A szelesseget CSS-valtozobol vesszuk, hogy csak asztalon hasson.

                Mobilon az oldalsav fedo fiok, ott a fix szelesseg a helyes -
                egy huzhato fogantyu erintokepernyon ugyis csak a gorgetessel
                versengene.
              */
                style={{ '--sidebar-w': `${width}px` } as React.CSSProperties}
                className={[
                  'absolute inset-0 z-10 transition-transform duration-(--duration-base) ease-(--ease-soft)',
                  'md:static md:z-auto md:translate-x-0 md:transition-[width,opacity]',
                  atHome ? 'translate-x-0' : '-translate-x-1/4',
                  collapsed
                    ? 'md:w-0 md:overflow-clip md:opacity-0'
                    : 'md:w-(--sidebar-w) md:opacity-100',
                ].join(' ')}
              >
                <Sidebar
                  me={me}
                  onOpenSwitcher={() => setSwitcherOpen(true)}
                  onCollapse={() => setCollapsed(true)}
                />
              </div>

              {/*
              A HUZHATO SZEL.

              Csak asztalon, es csak nyitott oldalsavnal: osszecsukott
              allapotban nincs mit merni. A fogantyu keskeny, de a kurzor
              (`col-resize`) es a nagyobb erzekeny sav elarulja, hogy meg
              lehet fogni.

              Billentyuvel is mukodik: a nyilak 16 keppontonkent lepnek. Egy
              csak egerrel hasznalhato vezerlo azoknak, akik billentyuvel
              dolgoznak, egyszeruen nem letezik.
            */}
              {!collapsed && (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Oldalsáv szélessége"
                  tabIndex={0}
                  onPointerDown={startResize}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowLeft') setWidth((w) => clampWidth(w - 16));
                    if (event.key === 'ArrowRight') setWidth((w) => clampWidth(w + 16));
                  }}
                  className="hidden w-1 shrink-0 cursor-col-resize bg-border-soft transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none md:block"
                />
              )}

              {/*
              A TARTALOM mobilon a lista FOLE usztat be jobbrol.

              A lista kozben egy negyeddel balra csuszik: ettol lesz melysege a
              mozdulatnak, es latszik, hogy a lista ott maradt mogotte.
            */}
              <main
                className={[
                  'absolute inset-0 z-20 flex min-w-0 flex-col overflow-clip bg-surface',
                  'transition-transform duration-(--duration-base) ease-(--ease-soft)',
                  'md:static md:z-auto md:min-w-0 md:flex-1 md:translate-x-0 md:bg-transparent',
                  atHome ? 'translate-x-full' : 'translate-x-0',
                ].join(' ')}
              >
                {/*
                Az ertesitesi engedely - belepeskor.

                Itt van a helye, a tartalom folott: aki ertesitest kert, de a
                bongeszo nem adott ra engedelyt, arrol eddig SENKI nem szolt.
              */}
                <UpdateNotice />
                <MissedCallBanner />
                <NotificationNudge permission={desktop.permission} onRequest={desktop.request} />
                <InstallNudge />
                <ShareTargetDialog />
                <OutboxFlusher meId={me.id} />
                <div className="min-h-0 min-w-0 flex-1 overflow-clip">
                  <Outlet />
                </div>
              </main>
            </div>
          </LayoutContext.Provider>

          <HuddleBar meId={me.id} />
          <HuddleInvitePrompt />
          {/* Vevoszolgalati mod: lebego panel a tavoli asztal folott (docs/20). */}
          <ServiceHost meId={me.id} />
        </HuddleProvider>

        {switcherOpen && <QuickSwitcher me={me} onClose={() => setSwitcherOpen(false)} />}
      </RealtimeProvider>
    </ActiveTeamProvider>
  );
}

/** A kimeno sor kiuritese (data/outbox.ts) - a valos ideju kapcsolaton belul kell futnia. */
function OutboxFlusher({ meId }: { meId: string }) {
  useOutboxFlusher(meId);
  return null;
}
